import React, { useEffect, useRef, useState } from "react";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import { API_URL, LOGGED_USER, TOKEN } from "../../variables/constants";
import axios from "axios";
import axiosInstance from "../../variables/api/axiosInstance";
import { useNavigate } from "react-router-dom";

const brand = {
  primary: "#242166",
  accent: "#a957f7",
  soft: "#f4f7fb",
  border: "#e5e7eb",
};

const navLinkStyle = (isActive) => ({
  border: `1px solid ${brand.border}`,
  color: isActive ? "#fff" : brand.primary,
  background: isActive
    ? `linear-gradient(135deg, ${brand.primary}, ${brand.accent})`
    : "#fff",
  boxShadow: isActive
    ? "0 10px 24px rgba(36, 33, 102, 0.18)"
    : "0 6px 16px rgba(15, 23, 42, 0.08)",
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "10px 16px",
  marginRight: "12px",
});

const cardStyle = {
  border: `1px solid ${brand.border}`,
  boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
  background: "linear-gradient(180deg, #ffffff 0%, #f8faff 100%)",
};

const cardHeaderStyle = {
  background: "transparent",
  color: brand.primary,
  fontWeight: 700,
  borderBottom: `1px solid ${brand.border}`,
};

const primaryButtonStyle = {
  background: `linear-gradient(135deg, ${brand.primary}, ${brand.accent})`,
  borderColor: brand.primary,
  boxShadow: "0 10px 20px rgba(36, 33, 102, 0.22)",
};

const ghostButtonStyle = {
  borderColor: brand.border,
  color: brand.primary,
  boxShadow: "0 6px 16px rgba(15, 23, 42, 0.06)",
};

// Fonction utilitaire pour initialiser l'état d'un champ
const safeGetUserValue = (user, path) => {
  try {
    const keys = path.split(".");
    let value = user;
    for (const key of keys) {
      if (!value || typeof value !== "object") return "";
      value = value[key];
    }
    // Gérer le cas spécifique du contact (qui est un tableau)
    if (path === "personne.contact[0]") {
      return Array.isArray(user?.personne?.contact) &&
        user?.personne?.contact.length > 0
        ? user.personne.contact[0]
        : "";
    }
    return value || "";
  } catch (e) {
    return "";
  }
};

const Profile = () => {
  const [user, setUser] = useState(null); // Changement : initialisé à null pour la gestion du chargement
  const [professions, setProfessions] = useState([]);
  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    email: "",
    telephone: "",
    adresseResidence: "",
    profession: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const navigate = useNavigate();
  const lastInitializedUserKeyRef = useRef(null);

  const getStoredUser = () => {
    try {
      return JSON.parse(sessionStorage.getItem("user"));
    } catch {
      return null;
    }
  };

  const persistUser = (nextUser) => {
    try {
      sessionStorage.setItem("user", JSON.stringify(nextUser));
    } catch {
      // ignore storage errors
    }
  };

  const getSessionIdentifier = (u) =>
    u?.userName || u?.username || u?.email || u?.personne?.email;

  const sanitizeUserForUpdate = (u) => {
    if (!u || typeof u !== "object") return u;
    const cleaned = { ...u };
    delete cleaned.password;
    delete cleaned.motDePasse;
    delete cleaned.motdepasse;
    delete cleaned.mdp;
    delete cleaned.pass;
    delete cleaned.dateExpiration;
    delete cleaned.dateCreation;
    delete cleaned.dateModificationMotDePasse;
    return cleaned;
  };

  // 1. Chargement initial de l'utilisateur et des données
  useEffect(() => {
    // Initialisation de l'utilisateur depuis les constantes
    if (LOGGED_USER) {
      setUser(LOGGED_USER);
    }

    // Récupération des professions
    const fetchProfessions = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/parametre/type/PROFESSION`, {
          headers: {
            Authorization: `Bearer ${TOKEN}`,
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Methods": "GET, PUT, POST, DELETE, OPTIONS",
            "Access-Control-Allow-Headers":
              "Content-Type, Access-Control-Allow-Headers, Authorization, X-Requested-With",
          },
        });
        const sorted = [...res.data].sort((a, b) =>
          (a.libelleParametre || "").localeCompare(
            b.libelleParametre || "",
            "fr",
            { sensitivity: "base" }
          )
        );
        setProfessions(sorted);
      } catch (err) {
        console.error("Erreur lors de la récupération des professions :", err);
      }
    };

    fetchProfessions();
    // Nous laissons TOKEN et LOGGED_USER dans les dépendances s'ils peuvent changer.
    // Si ce sont des constantes fixes, on peut les retirer, mais il est plus sûr de les laisser.
  }, [TOKEN]);

  // Rafraîchit la session depuis le backend pour éviter d'afficher des données obsolètes après reload.
  useEffect(() => {
    const stored = getStoredUser();
    if (stored && !user) setUser(stored);
    const identifier = getSessionIdentifier(stored || user);
    if (!identifier) return;

    axiosInstance
      .get(`/api/utilisateur/session/${identifier}`)
      .then(({ data }) => {
        if (data) {
          setUser(data);
          persistUser(data);
        }
      })
      .catch((err) => {
        console.error("Erreur lors du rafraîchissement de la session :", err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Initialisation des données du formulaire une fois que l'utilisateur est chargé
  useEffect(() => {
    if (user) {
      const userKey = user?.codeId || user?.id || user?.userName || user?.email;
      if (lastInitializedUserKeyRef.current === userKey) {
        setIsLoading(false);
        return;
      }
      lastInitializedUserKeyRef.current = userKey;
      setFormData({
        prenom: safeGetUserValue(user, "personne.prenom"),
        nom: safeGetUserValue(user, "personne.nom"),
        email: safeGetUserValue(user, "email"),
        telephone: safeGetUserValue(user, "personne.contact[0]"),
        adresseResidence: safeGetUserValue(user, "personne.adresse"),
        profession: safeGetUserValue(user, "personne.profession"),
      });
      setIsLoading(false);
    }
  }, [user]);

  // Gestion des changements du formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Gestion de la soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError("");
    setSaveSuccess("");
    if (!user) return;

    const updatedPersonne = {
      ...(user?.personne || {}),
      prenom,
      nom,
      adresse: adresseResidence,
      profession,
      contact: telephone ? [telephone] : user?.personne?.contact || [],
    };

    const payload = sanitizeUserForUpdate({
      ...user,
      email,
      personne: updatedPersonne,
    });

    try {
      setIsSaving(true);
      const res = await axiosInstance.put(`/api/utilisateur`, payload);
      const nextUser = res?.data || { ...user, ...payload };
      setUser(nextUser);
      persistUser(nextUser);
      if (nextUser?.codeId && nextUser.codeId !== user?.codeId) {
        navigate("/mon-profil", { replace: true });
      }
      setSaveSuccess("Profil mis à jour avec succès.");
    } catch (err) {
      console.error("Erreur lors de la mise à jour du profil :", err);
      setSaveError(
        "Impossible de mettre à jour le profil. Vérifiez vos informations ou réessayez plus tard."
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading && !user) {
    return (
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <SideBar />
          <div className="layout-page">
            <NavBar />
            <div className="content-wrapper">
              <div className="container-xxl flex-grow-1 container-p-y">
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                  <p className="mt-2">Chargement du profil...</p>
                </div>
              </div>
              <Footer />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Utilisation de la déstructuration pour simplifier l'accès aux données
  const { prenom, nom, email, telephone, adresseResidence, profession } =
    formData;
  const organismeRaisonSociale = user?.profil?.organisme?.raisonSociale || "N/A";
  const agenceVille = user?.agence?.ville || "N/A";
  const agenceCheminAcces = user?.agence?.cheminAcces || "";

  return (
    <div
      className="layout-wrapper layout-content-navbar"
      style={{ background: brand.soft }}
    >
      <div className="layout-container">
        <SideBar />

        <div className="layout-page">
          <NavBar />

          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <h4 className="fw-bold py-3 mb-4" style={{ color: brand.primary }}>
                <span className="text-muted fw-light">Mon Profil /</span> Compte
              </h4>
              <div className="row">
                <div className="col-md-12">
                  <div className="nav-align-top">
                    {/* Onglets de navigation */}
                    <ul
                      className="nav nav-pills flex-column flex-md-row mb-6"
                      style={{
                        background: "#fff",
                        padding: "10px",
                        borderRadius: "14px",
                        border: `1px solid ${brand.border}`,
                        boxShadow: "0 8px 22px rgba(15, 23, 42, 0.06)",
                      }}
                    >
                      <li className="nav-item">
                        <a
                          className="nav-link active"
                          href="/mon-profil"
                          style={navLinkStyle(true)}
                        >
                          <i className="bx bx-sm bx-user me-1_5"></i> Compte
                        </a>
                      </li>
                      <li className="nav-item">
                        <a
                          className="nav-link"
                          href="/mon-profil-securite"
                          style={navLinkStyle(false)}
                        >
                          <i className="bx bx-sm bx-lock-alt me-1_5"></i>{" "}
                          Sécurité
                        </a>
                      </li>
                      <li className="nav-item">
                        <a
                          className="nav-link"
                          href="/mon-profil-notifications"
                          style={navLinkStyle(false)}
                        >
                          <i className="bx bx-sm bx-bell me-1_5"></i>{" "}
                          Notifications
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div className="card mb-6" style={cardStyle}>
                    {/* Entête de la carte et avatar */}
                    <div className="card-header" style={cardHeaderStyle}>
                      <h5 className="mb-0">Informations du profil</h5>
                    </div>
                    <div className="card-body">
                      {saveError && (
                        <div className="alert alert-danger" role="alert">
                          {saveError}
                        </div>
                      )}
                      {saveSuccess && (
                        <div className="alert alert-success" role="alert">
                          {saveSuccess}
                        </div>
                      )}
                      <div className="d-flex align-items-start align-items-sm-center gap-6 pb-4 border-bottom">
                        <img
                          src="../assets/img/avatars/uploadAvatar.png"
                          alt="user-avatar"
                          className="d-block w-px-100 h-px-100 rounded object-fit-cover"
                          id="uploadedAvatar"
                        />
                        <div className="button-wrapper">
                          <label
                            htmlFor="upload"
                            className="btn btn-primary me-3 mb-2"
                            tabIndex="0"
                            style={primaryButtonStyle}
                          >
                            <span className="d-none d-sm-block">
                              Téléverser une nouvelle photo
                            </span>
                            <i className="bx bx-upload d-block d-sm-none"></i>
                            <input
                              type="file"
                              id="upload"
                              className="account-file-input"
                              hidden
                              accept="image/png, image/jpeg"
                            />
                          </label>
                          {/* <button
                            type="button"
                            className="btn btn-outline-secondary account-image-reset mb-2"
                            style={ghostButtonStyle}
                          >
                            <i className="bx bx-reset d-block d-sm-none"></i>
                            <span className="d-none d-sm-block">
                              Réinitialiser
                            </span>
                          </button> */}

                          <div className="text-muted small mt-2">
                            JPG, GIF ou PNG autorisés. Taille max de 800 Ko
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Formulaire des détails du compte */}
                    <div className="card-body pt-4">
                      <form id="formAccountSettings" onSubmit={handleSubmit}>
                        <div className="row g-6">
                          {/* Prénom(s) */}
                          <div className="col-md-6">
                            <label htmlFor="prenom" className="form-label">
                              Prénom(s)
                            </label>
                            <input
                              className="form-control"
                              type="text"
                              id="prenom"
                              name="prenom"
                              value={prenom}
                              onChange={handleChange}
                              autoFocus
                              required
                            />
                          </div>
                          {/* Nom */}
                          <div className="col-md-6">
                            <label htmlFor="nom" className="form-label">
                              Nom
                            </label>
                            <input
                              className="form-control"
                              type="text"
                              name="nom"
                              id="nom"
                              value={nom}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          {/* E-mail */}
                          <div className="col-md-6">
                            <label htmlFor="email" className="form-label">
                              E-mail
                            </label>
                            <input
                              className="form-control"
                              type="email"
                              id="email"
                              name="email"
                              value={email}
                              onChange={handleChange}
                              placeholder="john.doe@example.com"
                              required
                            />
                          </div>
                          {/* Organisme (Désactivé) */}
                          <div className="col-md-6">
                            <label htmlFor="organization" className="form-label">
                              Organisme
                            </label>
                            <input
                              type="text"
                              className="form-control bg-light" // Ajout de bg-light pour visuellement indiquer que c'est désactivé
                              id="organization"
                              name="organization"
                              value={organismeRaisonSociale}
                              disabled
                            />
                          </div>
                          {/* Numéro de téléphone */}
                          <div className="col-md-6">
                            <label className="form-label" htmlFor="telephone">
                              Numéro de téléphone
                            </label>
                            <div className="input-group input-group-merge">
                              <span className="input-group-text">
                                TG (+228)
                              </span>
                              <input
                                type="tel" // Changer type à tel
                                id="telephone"
                                name="telephone"
                                className="form-control"
                                placeholder="90 00 00 00"
                                value={telephone}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                          {/* Adresse de résidence */}
                          <div className="col-md-6">
                            <label
                              htmlFor="adresseResidence"
                              className="form-label"
                            >
                              Adresse de résidence
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="adresseResidence"
                              name="adresseResidence"
                              placeholder="Adresse de résidence"
                              value={adresseResidence}
                              onChange={handleChange}
                            />
                          </div>
                          {/* Ville (Désactivé) */}
                          <div className="col-md-6">
                            <label className="form-label" htmlFor="ville">
                              Ville
                            </label>
                            <input
                              type="text"
                              className="form-control bg-light"
                              id="ville"
                              name="ville"
                              value={agenceVille}
                              disabled
                            />
                          </div>
                          {/* Profession */}
                          <div className="col-md-6">
                            <label htmlFor="profession" className="form-label">
                              Profession
                            </label>
                            <select
                              id="profession"
                              name="profession"
                              className="form-select select2"
                              value={profession}
                              onChange={handleChange}
                              required
                            >
                              <option value="">
                                Sélectionner la profession
                              </option>
                              {professions.map((prof, id) => (
                                <option
                                  key={id}
                                  value={prof.libelleParametre}
                                >
                                  {prof.libelleParametre}
                                </option>
                              ))}
                            </select>
                          </div>
                          {/* Chemin d'accès (Désactivé ou à éditer si pertinent) */}
                          <div className="col-md-6">
                            <label htmlFor="cheminAcces" className="form-label">
                              Chemin d'accès (GPX)
                            </label>
                            <input
                              type="text"
                              className="form-control bg-light"
                              id="cheminAcces"
                              name="cheminAcces"
                              placeholder="Chemin d'accès aux fichiers de GPX"
                              value={agenceCheminAcces}
                              disabled
                            />
                          </div>
                        </div>
                        {/* Boutons d'action */}
                        <div className="mt-6">
                          <button
                            type="submit"
                            className="btn btn-primary me-3"
                            style={primaryButtonStyle}
                            disabled={isSaving}
                          >
                            {isSaving ? "Sauvegarde..." : "Sauvegarder les changements"}
                          </button>
                          <button
                            type="reset"
                            className="btn btn-outline-secondary"
                            style={ghostButtonStyle}
                            onClick={() => {
                              // Logique pour annuler les changements (recharger l'état initial)
                              if (user) {
                                setFormData({
                                  prenom: safeGetUserValue(user, "personne.prenom"),
                                  nom: safeGetUserValue(user, "personne.nom"),
                                  email: safeGetUserValue(user, "email"),
                                  telephone: safeGetUserValue(
                                    user,
                                    "personne.contact[0]"
                                  ),
                                  adresseResidence: safeGetUserValue(
                                    user,
                                    "personne.adresse"
                                  ),
                                  profession: safeGetUserValue(
                                    user,
                                    "personne.profession"
                                  ),
                                });
                              }
                            }}
                          >
                            Annuler
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                  {/* ... Le reste du code non modifié (Section Désactiver le compte) ... */}
                </div>
              </div>
            </div>

            <Footer />
            <div className="content-backdrop fade"></div>
          </div>
        </div>
      </div>
      <div className="layout-overlay layout-menu-toggle"></div>
      <div className="drag-target"></div>
    </div>
  );
};

export default Profile;
